import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Scales, MapTrifold, Wrench, PiggyBank, UsersThree } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import NewsletterBand from "@/components/NewsletterBand";

const SECTIONS = [
  {
    icon: Scales, title: "UK E-Bike Law & Regulations",
    blurb: "The rules that decide what you can ride, where, and what paperwork you need.",
    links: [
      { label: "GOV.UK — Electric bike rules (EAPC)", href: "https://www.gov.uk/electric-bike-rules", ext: true },
      { label: "GOV.UK — Vehicle registration (DVLA)", href: "https://www.gov.uk/vehicle-registration", ext: true },
      { label: "UK E-Bike Laws 2026: The Complete Guide", href: "/blog/ebike-laws-uk-2026" },
      { label: "Sur-Ron UK Legal Guide: Where Can You Actually Ride?", href: "/blog/surron-legal-guide-uk-2026" },
    ],
  },
  {
    icon: MapTrifold, title: "Cycling Routes & Where to Ride",
    blurb: "Hand-picked resources for planning your next ride, from Kent lanes to national trails.",
    links: [
      { label: "Sustrans — National Cycle Network", href: "https://www.sustrans.org.uk/national-cycle-network", ext: true },
      { label: "Explore Kent — Cycling routes", href: "https://explorekent.org/cycling/", ext: true },
      { label: "Cycling UK — Routes and rides", href: "https://www.cyclinguk.org/routes", ext: true },
      { label: "Best Cycling Routes in Kent 2026", href: "/blog/best-cycling-routes-kent-2026" },
    ],
  },
  {
    icon: Wrench, title: "Maintenance & Battery Care",
    blurb: "Keep your e-bike running like new with our expert workshop guides.",
    links: [
      { label: "E-Bike Maintenance Schedule 2026", href: "/blog/ebike-maintenance-schedule-2026" },
      { label: "E-Bike Battery Guide: Range, Care & Lifespan", href: "/blog/ebike-battery-guide-2026" },
      { label: "Sur-Ron Battery Maintenance Guide", href: "/blog/surron-battery-maintenance-guide" },
      { label: "Creo SL 2 Maintenance & Battery Care", href: "/blog/creo-sl2-maintenance-battery-care" },
    ],
  },
  {
    icon: PiggyBank, title: "Buying, Finance & Insurance",
    blurb: "Make the numbers work before you commit to your next bike.",
    links: [
      { label: "Cyclescheme — Cycle to Work explained", href: "https://www.cyclescheme.co.uk/", ext: true },
      { label: "E-Bike Financing Options UK 2026", href: "/blog/ebike-financing-uk-2026" },
      { label: "E-Bike Insurance UK: What You Actually Need", href: "/blog/ebike-insurance-uk-2026" },
      { label: "Best E-Bikes UK 2026: Expert Picks", href: "/blog/best-ebikes-uk-2026" },
    ],
  },
  {
    icon: UsersThree, title: "Community & Clubs",
    blurb: "Riding is better together — find your people.",
    links: [
      { label: "British Cycling — Find a club", href: "https://www.britishcycling.org.uk/clubfinder", ext: true },
      { label: "Cycling UK — Local groups", href: "https://www.cyclinguk.org/local-groups", ext: true },
      { label: "Our partner clubs across Kent", href: "/partners" },
      { label: "E-Bike Commuting Guide UK 2026", href: "/blog/ebike-commuting-guide-uk-2026" },
    ],
  },
];

export default function Resources() {
  return (
    <>
      <SEO
        title="Cycling Resources — Routes, UK E-Bike Law & Guides"
        description="A curated library of UK e-bike resources: official EAPC law links, Kent cycling routes, maintenance guides, financing and community groups — maintained by Ebikes&Gear."
        path="/resources"
      />
      <PageHero title="Cycling Resources" subtitle="Everything worth bookmarking: official UK e-bike law, route planners, maintenance guides and community links — curated by our workshop team." crumbs={[{ label: "Resources" }]} />

      <section className="container-px py-16" data-testid="resources-page">
        <div className="grid gap-6 lg:grid-cols-2">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={(i % 2) * 0.07}>
              <div className="h-full rounded-3xl border border-border bg-card p-7" data-testid={`resource-section-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-primary/10 text-primary"><s.icon size={22} weight="bold" /></span>
                  <h2 className="text-xl font-black tracking-tight">{s.title}</h2>
                </div>
                <p className="mt-3 text-sm text-muted-foreground">{s.blurb}</p>
                <ul className="mt-5 space-y-3">
                  {s.links.map((l) => (
                    <li key={l.label}>
                      {l.ext ? (
                        <a href={l.href} target="_blank" rel="noopener noreferrer" className="group inline-flex items-start gap-2 text-sm font-semibold text-foreground/90 hover:text-primary transition-colors">
                          <ArrowUpRight size={15} className="mt-0.5 shrink-0 text-primary" /> {l.label}
                        </a>
                      ) : (
                        <Link to={l.href} className="group inline-flex items-start gap-2 text-sm font-semibold text-foreground/90 hover:text-primary transition-colors">
                          <ArrowRight size={15} className="mt-0.5 shrink-0 text-primary transition-transform group-hover:translate-x-0.5" /> {l.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12"><NewsletterBand /></Reveal>
      </section>
    </>
  );
}
